import React from 'react';
import { GraduationCap, Building2 } from 'lucide-react';
import { useAudience } from '../contexts/AudienceContext';

/**
 * Zomato-style mode switch: Students (default) ↔ Schools
 * @param {boolean} compact - icon-only for narrow mobile headers (full labels in aria-label)
 */
const AudienceToggle = ({ className = '', compact = false }) => {
  const { audience, setAudience } = useAudience();
  const isSchool = audience === 'school';

  if (compact) {
    return (
      <div
        className={`inline-flex items-center rounded-full border border-blue-200/80 bg-gradient-to-r from-sky-50/90 to-teal-50/90 p-0.5 shadow-sm shrink-0 ${className}`}
        role="group"
        aria-label="Choose website mode: Students or Schools"
      >
        <button
          type="button"
          onClick={() => setAudience('student')}
          aria-pressed={!isSchool}
          aria-label="Students, career guidance for learners"
          title="Students"
          className={`flex h-10 w-10 items-center justify-center rounded-full transition-all duration-300 ${
            !isSchool
              ? 'bg-gradient-to-r from-blue-600 to-teal-600 text-white shadow-md'
              : 'text-gray-600 hover:bg-white/80'
          }`}
        >
          <GraduationCap className={`h-5 w-5 ${!isSchool ? 'text-white' : 'text-blue-600'}`} />
        </button>
        <button
          type="button"
          onClick={() => setAudience('school')}
          aria-pressed={isSchool}
          aria-label="Schools, partnerships and demos"
          title="Schools"
          className={`flex h-10 w-10 items-center justify-center rounded-full transition-all duration-300 ${
            isSchool
              ? 'bg-gradient-to-r from-blue-600 to-teal-600 text-white shadow-md'
              : 'text-gray-600 hover:bg-white/80'
          }`}
        >
          <Building2 className={`h-5 w-5 ${isSchool ? 'text-white' : 'text-teal-600'}`} />
        </button>
      </div>
    );
  }

  return (
    <div
      className={`flex items-center rounded-full border border-blue-200/80 bg-gradient-to-r from-sky-50/90 to-teal-50/90 p-1 shadow-sm backdrop-blur-sm ${className}`}
      role="group"
      aria-label="Choose website mode"
    >
      <button
        type="button"
        onClick={() => setAudience('student')}
        aria-pressed={!isSchool}
        className={`relative flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-all duration-300 md:gap-2 md:px-4 md:py-2 md:text-sm ${
          !isSchool
            ? 'bg-gradient-to-r from-blue-600 to-teal-600 text-white shadow-md'
            : 'text-gray-600 hover:text-blue-700'
        }`}
      >
        <GraduationCap className={`h-3.5 w-3.5 md:h-4 md:w-4 ${!isSchool ? 'text-white' : 'text-blue-600'}`} />
        <span className="whitespace-nowrap">Students</span>
      </button>
      <button
        type="button"
        onClick={() => setAudience('school')}
        aria-pressed={isSchool}
        className={`relative flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-all duration-300 md:gap-2 md:px-4 md:py-2 md:text-sm ${
          isSchool
            ? 'bg-gradient-to-r from-blue-600 to-teal-600 text-white shadow-md'
            : 'text-gray-600 hover:text-teal-700'
        }`}
      >
        <Building2 className={`h-3.5 w-3.5 md:h-4 md:w-4 ${isSchool ? 'text-white' : 'text-teal-600'}`} />
        <span className="whitespace-nowrap">Schools</span>
      </button>
    </div>
  );
};

export default AudienceToggle;
